/**
 * Odrezky pre túto zákazku v okne Poslať do Cut Rite (KOLO 178).
 *
 * User: *„operátor pri píle si všimne, že má tabuľu alebo odrezok (napr.
 * 2000 × 1500) … ja zadám rozmer a zákazku znova optimalizujem. Sklad
 * odrezkov nechcem."* Pomocník odrezok pri odoslaní pridá do Seznamu
 * tabulí úlohy, nie do Knižnice tabúľ — v iných zákazkách sa neponúkne.
 * Odrezok ostáva pri zákazke, kým ho niekto nezmaže, a ide s každým
 * ďalším odoslaním. Pozri `docs/rozpocet/CUTRITE.md` → Odrezky.
 */
import { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { usePridajOdrezok, useZmazOdrezok } from "@/api/hooks/useCutrite";
import type {
  CutriteMaterialOdrezku,
  CutriteOdrezok,
  CutriteOdrezokOdoslany,
} from "@/api/schemas";

export function pocetOdrezkov(n: number): string {
  if (n === 1) return "1 odrezok";
  if (n >= 2 && n <= 4) return `${n} odrezky`;
  return `${n} odrezkov`;
}

interface Formular {
  material_id: string;
  dlzka: string;
  sirka: string;
  pocet: string;
}

type ChybyFormulara = Partial<Record<keyof Formular, string>>;

const CELE_KLADNE = /^\d+$/;

/** Kladné celé mm a aspoň 1 kus — to isté overí server (422). */
export function chybyOdrezku(f: Formular): ChybyFormulara {
  const chyby: ChybyFormulara = {};
  if (!f.material_id) chyby.material_id = "Vyber materiál.";
  for (const [pole, nazov] of [
    ["dlzka", "Dĺžka"],
    ["sirka", "Šírka"],
  ] as const) {
    const v = f[pole].trim();
    if (!CELE_KLADNE.test(v) || Number(v) <= 0)
      chyby[pole] = `${nazov} je kladné celé číslo v mm.`;
  }
  const p = f.pocet.trim();
  if (!CELE_KLADNE.test(p) || Number(p) < 1) chyby.pocet = "Počet je aspoň 1.";
  return chyby;
}

/** Čo sa s odrezkom v pláne stalo — po optimalizácii (KOLO 178). */
export function vetaOdrezku(r: CutriteOdrezokOdoslany): string {
  const co = `Odrezok ${r.material} ${r.dlzka} × ${r.sirka}`;
  if (r.pouzite == null) return `${co} išiel do Seznamu tabulí — použitie Cut Rite nehlási.`;
  if (r.pouzite === 0) return `${co} sa v pláne nepoužil.`;
  return `${co} sa v pláne použil (${r.pouzite}×).`;
}

const PRAZDNY: Formular = { material_id: "", dlzka: "", sirka: "", pocet: "1" };

export function CutriteOdrezky({
  zakazkaId,
  varianty,
  odrezky,
  materialy,
  staryPomocnik,
  nadpis = "Odrezky pre túto zákazku",
}: {
  zakazkaId: string;
  /** výber okna — odrezok patrí zoznamu týchto variant (KOLO 207) */
  varianty: readonly string[];
  odrezky: CutriteOdrezok[];
  materialy: CutriteMaterialOdrezku[];
  /** na PC beží pomocník bez odrezkov — zákazku s odrezkom nedostane */
  staryPomocnik: boolean;
  /** pri zákazke s viac variantami „Odrezky pre tento zoznam" (KOLO 207) */
  nadpis?: string;
}): JSX.Element {
  const pridaj = usePridajOdrezok(zakazkaId, varianty);
  const zmaz = useZmazOdrezok(zakazkaId, varianty);
  const [formular, setFormular] = useState<Formular | null>(null);
  const [skusil, setSkusil] = useState(false);

  const chyby = formular ? chybyOdrezku(formular) : {};
  const platny = formular !== null && Object.keys(chyby).length === 0;

  function otvor(): void {
    // Jeden materiál = rovno vybraný; inak nech user vyberie vedome
    setFormular({
      ...PRAZDNY,
      material_id: materialy.length === 1 ? (materialy[0]?.material_id ?? "") : "",
    });
    setSkusil(false);
  }

  async function uloz(): Promise<void> {
    setSkusil(true);
    if (!formular || !platny) return;
    try {
      await pridaj.mutateAsync({
        material_id: formular.material_id,
        dlzka_mm: Number(formular.dlzka),
        sirka_mm: Number(formular.sirka),
        pocet: Number(formular.pocet),
      });
      setFormular(null);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Odrezok sa nepodarilo pridať",
        description: extractErrorMessage(err),
      });
    }
  }

  async function odober(o: CutriteOdrezok): Promise<void> {
    try {
      await zmaz.mutateAsync(o.id);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Odrezok sa nepodarilo zmazať",
        description: extractErrorMessage(err),
      });
    }
  }

  const ukazChybu = (pole: keyof Formular): string | undefined =>
    skusil || (formular && formular[pole] !== PRAZDNY[pole]) ? chyby[pole] : undefined;

  return (
    <section
      className="min-w-0 space-y-2 rounded-md border p-3"
      aria-label={nadpis}
      data-testid="cutrite-odrezky"
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium">{nadpis}</h3>
        {formular === null ? (
          <Button
            variant="outline"
            size="sm"
            onClick={otvor}
            disabled={materialy.length === 0}
            title={
              materialy.length === 0
                ? "Zákazka nemá plošný materiál s kódom pre Cut Rite"
                : undefined
            }
          >
            <Plus className="h-3.5 w-3.5" />
            Pridať odrezok
          </Button>
        ) : null}
      </div>

      {odrezky.length === 0 && formular === null ? (
        <p className="text-xs text-muted-foreground">
          Tabuľu alebo odrezok, ktorý operátor pri píle našiel, pridaj sem — pomocník ho
          pridá do Seznamu tabulí tejto úlohy, nie do Knižnice tabúľ.
        </p>
      ) : null}

      {odrezky.length > 0 ? (
        <ul className="space-y-1">
          {odrezky.map((o) => (
            <li
              key={o.id}
              className="flex items-center gap-2 text-sm"
              data-testid="cutrite-odrezok"
            >
              <span className="min-w-0 flex-1">
                <span className="font-mono">{o.material || o.material_nazov}</span> ·{" "}
                {o.dlzka_mm} × {o.sirka_mm} · {o.pocet} ks
                {/* Odrezok z programu pri píle má autora Píla (KOLO 179) */}
                {o.autor ? (
                  <span className="text-xs text-muted-foreground"> · zadal {o.autor}</span>
                ) : null}
                {o.tabula ? null : (
                  <span className="text-destructive"> — materiál nie je v zákazke</span>
                )}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                aria-label={`Zmazať odrezok ${o.tabula ?? `${o.dlzka_mm} × ${o.sirka_mm}`}`}
                onClick={() => void odober(o)}
                disabled={zmaz.isPending}
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {formular !== null ? (
        <div className="space-y-2 border-t pt-2" data-testid="cutrite-odrezok-formular">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,2fr)_1fr_1fr_5rem]">
            <div className="min-w-0 space-y-1">
              <Label htmlFor="odrezok-material" className="text-xs">
                Materiál
              </Label>
              <select
                id="odrezok-material"
                value={formular.material_id}
                onChange={(e) => setFormular({ ...formular, material_id: e.target.value })}
                className="h-9 w-full rounded-md border bg-background px-2 font-mono text-sm"
                aria-invalid={Boolean(ukazChybu("material_id"))}
              >
                <option value="">Vyber materiál zákazky…</option>
                {materialy.map((m) => (
                  <option key={m.material_id} value={m.material_id}>
                    {m.kod} — {m.nazov}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="odrezok-dlzka" className="text-xs">
                Dĺžka (mm)
              </Label>
              <Input
                id="odrezok-dlzka"
                inputMode="numeric"
                value={formular.dlzka}
                onChange={(e) => setFormular({ ...formular, dlzka: e.target.value })}
                aria-invalid={Boolean(ukazChybu("dlzka"))}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="odrezok-sirka" className="text-xs">
                Šírka (mm)
              </Label>
              <Input
                id="odrezok-sirka"
                inputMode="numeric"
                value={formular.sirka}
                onChange={(e) => setFormular({ ...formular, sirka: e.target.value })}
                aria-invalid={Boolean(ukazChybu("sirka"))}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="odrezok-pocet" className="text-xs">
                Počet (ks)
              </Label>
              <Input
                id="odrezok-pocet"
                inputMode="numeric"
                value={formular.pocet}
                onChange={(e) => setFormular({ ...formular, pocet: e.target.value })}
                aria-invalid={Boolean(ukazChybu("pocet"))}
                className="h-9"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Dĺžka je v smere vlákna ako pri plnej tabuli.
          </p>
          {(["material_id", "dlzka", "sirka", "pocet"] as const)
            .map(ukazChybu)
            .filter(Boolean)
            .map((c) => (
              <p key={c} className="text-xs text-destructive" data-testid="cutrite-odrezok-chyba">
                {c}
              </p>
            ))}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setFormular(null)}>
              Zrušiť
            </Button>
            <Button size="sm" onClick={() => void uloz()} disabled={!platny || pridaj.isPending}>
              {pridaj.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Uložiť odrezok
            </Button>
          </div>
        </div>
      ) : null}

      {staryPomocnik && odrezky.length > 0 ? (
        <p
          className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-900 dark:text-amber-200"
          data-testid="cutrite-odrezky-stary-pomocnik"
        >
          Na PC s Cut Rite beží starý pomocník — odrezok nepozná, zákazku s odrezkom
          preto nedostane. Najprv ho aktualizuj (Nastavenia → Prepojenia → Pomocník pre
          Cut Rite).
        </p>
      ) : null}
    </section>
  );
}
